import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

// Pages
import HomePage from "@/pages/HomePage";
import InventoryPage from "@/pages/InventoryPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import CreditApplicationPage from "@/pages/CreditApplicationPage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import BlogPage from "@/pages/BlogPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFoundPage from "@/pages/NotFoundPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import FAQPage from "@/pages/FAQPage";
import SMSTermsPage from "@/pages/SMSTermsPage";
import CreditApplicationPage2 from "@/pages/CreditApplicationPage2";
import ReferralProgramPage from "@/pages/ReferralProgramPage";
import DiscoveryFAQsPage from "@/pages/DiscoveryFAQsPage";

// Components
import ChatWidget from "@/components/ChatWidget";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// reCAPTCHA site key - use placeholder if not set (allows login without captcha in dev)
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Auth Context
export const AuthContext = React.createContext(null);

function App() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    
    // Check for saved auth
    const token = localStorage.getItem('adminToken');
    const savedAdmin = localStorage.getItem('admin');
    if (token && savedAdmin) {
      setAdmin(JSON.parse(savedAdmin));
    }
    setLoading(false);
  }, []);

  const login = (token, adminData) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('admin', JSON.stringify(adminData));
    setAdmin(adminData);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    setAdmin(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ admin, login, logout }}>
      <div className="App min-h-screen bg-[#0a1628]">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Wrap admin routes with reCAPTCHA provider only if key is set
  const renderAdminLogin = () => {
    if (RECAPTCHA_SITE_KEY) {
      return (
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
          <AdminLoginPage />
        </GoogleReCaptchaProvider>
      );
    }
    return <AdminLoginPage />;
  };

  return (
    <>
      <ScrollToTop />
      {!isAdminRoute && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/inventory/:stockNumber" element={<VehicleDetailPage />} />
        <Route path="/vehicle/:id" element={<VehicleDetailPage />} /> {/* Legacy route */}
        <Route path="/credit-application" element={<CreditApplicationPage />} />
        <Route path="/credit-application2" element={<CreditApplicationPage2 />} />
        <Route path="/contact-us" element={<ContactPage />} />
        <Route path="/contact" element={<ContactPage />} /> {/* Legacy redirect */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/sms-terms" element={<SMSTermsPage />} />
        <Route path="/referral-program" element={<ReferralProgramPage />} />
        <Route path="/discovery-faqs" element={<DiscoveryFAQsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/admin/login" element={renderAdminLogin()} />
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ChatWidget />}
    </>
  );
}

export default App;
export { API, BACKEND_URL };
